import { useState, useEffect, useRef, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import {
  Pause,
  Play,
  ArrowDown,
  X,
  Search,
  AlertCircle,
  Loader2,
  FlaskConical,
  Server,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

interface LogsTabProps {
  projectId: string
  branchId: number
  branchType: string
}

// ANSI color code mapping for Odoo logs
const ansiColors: Record<string, string> = {
  '30': 'color: #000000',
  '31': 'color: #cc0000',
  '32': 'color: #00cc00',
  '33': 'color: #cccc00',
  '34': 'color: #0000cc',
  '35': 'color: #cc00cc',
  '36': 'color: #00cccc',
  '37': 'color: #cccccc',
  '90': 'color: #666666',
  '91': 'color: #ff0000',
  '92': 'color: #00ff00',
  '93': 'color: #ffff00',
  '94': 'color: #0066ff',
  '95': 'color: #ff00ff',
  '96': 'color: #00ffff',
  '97': 'color: #ffffff',
  '1': 'font-weight: bold',
  '4': 'text-decoration: underline',
  '0': '', // Reset
}

// Parse ANSI codes and convert to HTML spans
function parseAnsiToHtml(text: string): string {
  let processed = text.replace(/\n/g, '\n').replace(/\t/g, '  ')

  processed = processed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  const ansiRegex = /\x1b\[([0-9;]+)m/g
  let result = ''
  let lastIndex = 0
  let match
  let openSpans = 0

  while ((match = ansiRegex.exec(processed)) !== null) {
    result += processed.slice(lastIndex, match.index)

    const codes = match[1].split(';')

    for (const code of codes) {
      if (code === '0' || code === '') {
        while (openSpans > 0) {
          result += '</span>'
          openSpans--
        }
      } else if (ansiColors[code]) {
        result += `<span style="${ansiColors[code]}">`
        openSpans++
      }
    }

    lastIndex = ansiRegex.lastIndex
  }

  result += processed.slice(lastIndex)

  while (openSpans > 0) {
    result += '</span>'
    openSpans--
  }

  return result
}

// Format log line with timestamp highlighting
function formatLogLine(line: string): string {
  const odooLogRegex =
    /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3})\s+(INFO|WARNING|ERROR|DEBUG|CRITICAL)\s+(\S+)\s+(\S+):\s*(.*)$/
  const match = line.match(odooLogRegex)

  if (match) {
    const [, timestamp, level, db, module, message] = match
    const levelColors: Record<string, string> = {
      INFO: 'color: #008000',
      WARNING: 'color: #cc6600',
      ERROR: 'color: #cc0000',
      DEBUG: 'color: #666666',
      CRITICAL: 'color: #ff0000; font-weight: bold',
    }

    return `<span style="color: #666666">${timestamp}</span> <span style="${
      levelColors[level] || ''
    }">${level}</span> <span style="color: #0066cc">${db}</span> <span style="color: #cc00cc">${module}</span>: ${message}`
  }

  return parseAnsiToHtml(line)
}

export function LogsTab({ projectId, branchId, branchType }: LogsTabProps) {
  const [logs, setLogs] = useState<string[]>([])
  const [isPaused, setIsPaused] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(true)
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [logType, setLogType] = useState<'server' | 'test'>('server')
  const [autoScroll, setAutoScroll] = useState(true)

  const socketRef = useRef<Socket | null>(null)
  const logContainerRef = useRef<HTMLPreElement>(null)
  const pausedLogsRef = useRef<string[]>([])

  const logBufferRef = useRef<string[]>([])
  const bufferTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isDevelopment = branchType === 'development'

  const scrollToBottom = useCallback(() => {
    if (logContainerRef.current && autoScroll) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [autoScroll])

  // Track paused via ref (avoid recreating socket on pause toggles)
  const isPausedRef = useRef(isPaused)
  isPausedRef.current = isPaused

  // Track loading state via ref to avoid stale closures
  const isLoadingHistoryRef = useRef(true)

  // Track if this effect instance is still active (StrictMode double-invoke)
  const isActiveRef = useRef(true)

  // Connect to WebSocket
  useEffect(() => {
    isActiveRef.current = true
    let hasStartedStream = false

    setIsConnecting(true)
    setIsLoadingHistory(true)
    isLoadingHistoryRef.current = true
    setError(null)
    setLogs([])
    logBufferRef.current = []
    pausedLogsRef.current = []

    // Match the old Flask/Jinja websocket.js settings exactly:
    // Always use same origin to go through the proxy server
    // This ensures WebSocket connections work from browser → frontend proxy → backend
    const socketBase = window.location.origin;

    // Extract session cookie for auth (mirrors old websocket.js)
    const sessionId = document.cookie.match(/session=([^;]+)/)?.[1] || ''

    const socket = io(`${socketBase}/socket.io`, {
      path: '/socket.io/',
      withCredentials: true,
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
      timeout: 20000,
      autoConnect: true,
      forceNew: false,
      auth: {
        sessionId,
      },
    })

    socketRef.current = socket

    socket.on('connect', () => {
      if (!isActiveRef.current) {
        socket.disconnect()
        return
      }

      setIsConnected(true)
      setIsConnecting(false)
      socket.emit('join_project', { project_id: projectId })
    })

    const startLogStream = () => {
      if (!isActiveRef.current || hasStartedStream) return
      hasStartedStream = true
      socket.emit('start_log_stream', {
        deployment_id: branchId,
        log_type: logType,
      })
    }

    socket.on('joined_project', () => {
      startLogStream()
    })

    socket.on('room_joined', () => {
      startLogStream()
    })

    socket.on('log_update', (data: { log: string }) => {
      if (!isActiveRef.current) return

      const logLine = data.log
      if (isPausedRef.current) {
        pausedLogsRef.current.push(logLine)
        return
      }

      if (isLoadingHistoryRef.current) {
        isLoadingHistoryRef.current = false
        setIsLoadingHistory(false)
      }

      setLogs((prev) => [...prev, logLine])
    })

    socket.on('error', (data: { error: string }) => {
      if (!isActiveRef.current) return
      setError(data.error)
      setIsLoadingHistory(false)
      isLoadingHistoryRef.current = false
    })

    socket.on('connect_error', () => {
      if (!isActiveRef.current) return
      setIsConnecting(false)
      setIsLoadingHistory(false)
      isLoadingHistoryRef.current = false
      setError('Failed to connect to log server. Please try refreshing.')
    })

    socket.on('disconnect', () => {
      if (!isActiveRef.current) return
      setIsConnected(false)
    })

    return () => {
      isActiveRef.current = false
      if (bufferTimeoutRef.current) {
        clearTimeout(bufferTimeoutRef.current)
      }
      if (socket) {
        socket.emit('stop_log_stream', {
          deployment_id: branchId,
          log_type: logType,
        })
        socket.disconnect()
      }
      socketRef.current = null
    }
  }, [projectId, branchId, logType])

  // Auto-scroll when new logs arrive (but not during initial load to prevent jumping)
  useEffect(() => {
    if (!isLoadingHistory) {
      scrollToBottom()
    }
  }, [logs, scrollToBottom, isLoadingHistory])

  // Scroll to bottom after initial load completes
  useEffect(() => {
    if (!isLoadingHistory && logs.length > 0) {
      setTimeout(scrollToBottom, 50)
    }
  }, [isLoadingHistory, scrollToBottom, logs.length])

  // Resume logs - add paused logs
  const handleResume = () => {
    setIsPaused(false)
    if (pausedLogsRef.current.length > 0) {
      setLogs((prev) => [...prev, ...pausedLogsRef.current])
      pausedLogsRef.current = []
    }
  }

  // Switch log type
  const handleLogTypeChange = (newType: 'server' | 'test') => {
    if (newType === logType) return

    if (socketRef.current && isConnected) {
      socketRef.current.emit('stop_log_stream', {
        deployment_id: branchId,
        log_type: logType,
      })

      setLogs([])
      logBufferRef.current = []
      pausedLogsRef.current = []
      setIsLoadingHistory(true)
      setLogType(newType)

      socketRef.current.emit('start_log_stream', {
        deployment_id: branchId,
        log_type: newType,
      })
    }
  }

  const filteredLogs = searchQuery
    ? logs.filter((log) => log.toLowerCase().includes(searchQuery.toLowerCase()))
    : logs

  const handleScroll = () => {
    if (logContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50
      setAutoScroll(isAtBottom)
    }
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="mt-4">
      {/* Log Type Tabs (only for development builds) */}
      {isDevelopment && (
        <Tabs
          value={logType}
          onValueChange={(v) => handleLogTypeChange(v as 'server' | 'test')}
          className="mb-3"
        >
          <TabsList>
            <TabsTrigger value="server" className="gap-1.5">
              <Server className="h-4 w-4" />
              Server Logs
            </TabsTrigger>
            <TabsTrigger value="test" className="gap-1.5">
              <FlaskConical className="h-4 w-4" />
              Test Logs
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Log Controls */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          {isPaused ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResume}
              className="text-green-600 border-green-300 hover:bg-green-50"
            >
              <Play className="h-4 w-4 mr-1" />
              Resume
              {pausedLogsRef.current.length > 0 && (
                <span className="ml-1 text-xs">({pausedLogsRef.current.length} new)</span>
              )}
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setIsPaused(true)}>
              <Pause className="h-4 w-4 mr-1" />
              Pause
            </Button>
          )}

          {/* Connection Status */}
          <div className="flex items-center gap-1.5 text-sm">
            <span
              className={cn(
                'w-2 h-2 rounded-full',
                (isConnecting || isLoadingHistory) && 'bg-yellow-500 animate-pulse',
                isConnected && !isConnecting && !isLoadingHistory && 'bg-green-500',
                !isConnected && !isConnecting && !isLoadingHistory && 'bg-red-500',
              )}
            />
            <span className="text-gray-500">
              {isConnecting || isLoadingHistory ? 'Connecting...' : isConnected ? 'Live' : 'Disconnected'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative w-48">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
            {searchQuery && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Scroll to Bottom */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setAutoScroll(true)
              if (logContainerRef.current) {
                logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
              }
            }}
            disabled={autoScroll}
            title="Scroll to Bottom"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Log Container */}
      <pre
        ref={logContainerRef}
        onScroll={handleScroll}
        className="w-full h-[700px] bg-white text-green-700 p-3 rounded border border-gray-200 overflow-y-auto font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words"
        style={{ wordWrap: 'break-word', overflowWrap: 'anywhere' }}
      >
        {(isConnecting || (isLoadingHistory && logs.length === 0)) && (
          <div className="flex items-center justify-center h-full text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Connecting to log stream...
          </div>
        )}

        {!isConnecting && !isLoadingHistory && filteredLogs.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500">
            {searchQuery ? 'No matching logs found' : 'No logs available'}
          </div>
        )}

        {filteredLogs.map((line, index) => (
          <div key={index} dangerouslySetInnerHTML={{ __html: formatLogLine(line) }} />
        ))}
      </pre>

      {/* Footer info */}
      <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
        <span>
          {filteredLogs.length} lines
          {searchQuery && ` (filtered from ${logs.length})`}
        </span>
        {isPaused && <span className="text-yellow-600">Paused - {pausedLogsRef.current.length} new lines buffered</span>}
      </div>
    </div>
  )
}
